import type { ComputePositionConfig } from '@floating-ui/dom'
import { flip, limitShift, shift } from '@floating-ui/dom'
import { TOUR_EXPORT_LOCK_CLASS } from '@/components/product-tour/shepherdConstants'
import type { ShepherdTour } from '@/components/product-tour/utils/shepherdTourCancel'
import { triggerTourConfetti } from '@/components/product-tour/utils/tourConfetti'
import {
	isTourTargetEnabled,
	isTourTargetVisible,
	type WaitForTourTargetOptions,
	waitForElementAndScroll,
	waitForTourTarget,
	waitForTourTargets,
} from '@/components/product-tour/utils/tourDomWait'
import { TOUR } from '@/lib/product-tour/constants'

const GENERATION_TOUR_WAIT: WaitForTourTargetOptions = { timeoutMs: null }

export function applyProductTourShepherdSteps(opts: {
	tour: ShepherdTour
	stepIndex: number
	text: (key: string) => string
}) {
	const { tour, stepIndex: idx, text } = opts

	const centered = (
		id: string,
		titleKey: string,
		textKey: string,
		showNext = true,
		optsInner?: {
			beforeShowPromise?: () => Promise<unknown>
		}
	) => {
		tour.addStep({
			id,
			title: text(titleKey),
			text: text(textKey),
			canClickTarget: true,
			...(optsInner?.beforeShowPromise != null
				? { beforeShowPromise: optsInner.beforeShowPromise }
				: {}),
			...(showNext
				? {
						buttons: [
							{
								text: text('tour.scrum229.buttons.next'),
								classes: 'shepherd-button-primary',
								action() {
									tour.complete()
								},
							},
						],
					}
				: {}),
		})
	}

	const attachAdvance = (
		id: string,
		titleKey: string,
		textKey: string,
		selector: string,
		on: 'top' | 'bottom' | 'left' | 'right' = 'bottom',
		attachOpts?: {
			waitForTargetInDom?: boolean
			beforeShowPromise?: () => Promise<unknown>
			advanceOnClick?: boolean
			advanceOnSelector?: string
			extraHighlights?: string[]
			disableTargetClick?: boolean
			showNextButton?: boolean
			floatingUIOptions?: ComputePositionConfig
			tourTargetWait?: WaitForTourTargetOptions
		}
	) => {
		const advanceOnClick = attachOpts?.advanceOnClick !== false
		const advanceSelector = attachOpts?.advanceOnSelector ?? selector
		const shouldWait = attachOpts?.waitForTargetInDom !== false

		const buildDefaultBeforeShowPromise = async () => {
			if (shouldWait) {
				const requireEnabled =
					attachOpts?.tourTargetWait?.requireEnabled ?? (advanceOnClick && advanceSelector === selector)
				await waitForTourTarget(selector, {
					...attachOpts?.tourTargetWait,
					requireEnabled,
				})
			}
			if (advanceOnClick && advanceSelector !== selector) {
				await waitForTourTarget(advanceSelector, {
					...attachOpts?.tourTargetWait,
					requireEnabled: true,
				})
			}
		}

		const showWhenReady =
			attachOpts?.beforeShowPromise != null
				? { beforeShowPromise: attachOpts.beforeShowPromise }
				: shouldWait
					? { beforeShowPromise: buildDefaultBeforeShowPromise }
					: {}
		tour.addStep({
			id,
			title: text(titleKey),
			text: text(textKey),
			attachTo: { element: selector, on },
			...(attachOpts?.extraHighlights && attachOpts.extraHighlights.length > 0
				? { extraHighlights: attachOpts.extraHighlights }
				: {}),
			canClickTarget: attachOpts?.disableTargetClick ? false : true,
			...(advanceOnClick ? { advanceOn: { selector: advanceSelector, event: 'click' } } : {}),
			...showWhenReady,
			...(attachOpts?.floatingUIOptions ? { floatingUIOptions: attachOpts.floatingUIOptions } : {}),
			...(attachOpts?.showNextButton
				? {
						buttons: [
							{
								text: text('tour.scrum229.buttons.next'),
								classes: 'shepherd-button-primary',
								action() {
									tour.complete()
								},
							},
						],
					}
				: {}),
		})
	}

	switch (idx) {
		case 0:
			centered('s0', 'tour.scrum229.step01.title', 'tour.scrum229.step01.body', true)
			break
		case 1:
			attachAdvance(
				's1',
				'tour.scrum229.stepWorkspaceSwitcher.title',
				'tour.scrum229.stepWorkspaceSwitcher.body',
				`[data-tour="${TOUR.workspaceSwitcher}"]`,
				'bottom',
				{
					advanceOnClick: false,
					disableTargetClick: true,
					showNextButton: true,
					waitForTargetInDom: true,
				}
			)
			break
		case 2:
			attachAdvance(
				's2',
				'tour.scrum229.stepWorkspaceMembers.title',
				'tour.scrum229.stepWorkspaceMembers.body',
				`[data-tour="${TOUR.workspaceMembersAnchor}"]`,
				'right',
				{
					advanceOnClick: false,
					showNextButton: true,
					extraHighlights: [`[data-tour="${TOUR.workspaceMembers}"]`],
					beforeShowPromise: () => waitForElementAndScroll(`[data-tour="${TOUR.workspaceMembers}"]`),
					floatingUIOptions: {
						placement: 'right',
						middleware: [
							flip({
								fallbackPlacements: ['right', 'right-start', 'right-end', 'bottom', 'top'],
							}),
							shift({ limiter: limitShift(), crossAxis: true }),
						],
					},
				}
			)
			break
		case 3:
			attachAdvance(
				's3',
				'tour.scrum229.step02.title',
				'tour.scrum229.step02.body',
				`[data-tour="${TOUR.newProject}"]`
			)
			break
		case 4:
			attachAdvance(
				's4',
				'tour.scrum229.step04.title',
				'tour.scrum229.step04.body',
				`[data-tour="${TOUR.projectCreateModal}"]`,
				'right',
				{
					advanceOnClick: false,
					waitForTargetInDom: true,
					extraHighlights: [`[data-tour="${TOUR.projectCreate}"]`],
				}
			)
			break
		case 5:
			attachAdvance(
				's5',
				'tour.scrum229.step05.title',
				'tour.scrum229.step05.body',
				`[data-tour="${TOUR.projectTile}"]`,
				'bottom',
				{ waitForTargetInDom: true }
			)
			break
		case 6:
			centered('s6', 'tour.scrum229.step06a.title', 'tour.scrum229.step06a.body', true)
			break
		case 7:
			attachAdvance(
				's7',
				'tour.scrum229.step06b.title',
				'tour.scrum229.step06b.body',
				`[data-tour="${TOUR.filesCard}"]`,
				'right',
				{ advanceOnClick: false, showNextButton: true, waitForTargetInDom: true }
			)
			break
		case 8:
			attachAdvance(
				's8',
				'tour.scrum229.step06c.title',
				'tour.scrum229.step06c.body',
				`[data-tour="${TOUR.importFile}"]`,
				'top',
				{ advanceOnClick: false, disableTargetClick: true, showNextButton: true }
			)
			break
		case 9:
			attachAdvance(
				's9',
				'tour.scrum229.step06d.title',
				'tour.scrum229.step06d.body',
				`[data-tour="${TOUR.pasteText}"]`,
				'top'
			)
			break
		case 10:
			attachAdvance(
				's10',
				'tour.scrum229.step06e.title',
				'tour.scrum229.step06e.body',
				`[data-tour="${TOUR.pasteModal}"]`,
				'right',
				{
					waitForTargetInDom: true,
					advanceOnSelector: `[data-tour="${TOUR.pasteSubmit}"]`,
				}
			)
			break
		case 11: {
			const whenHandlers = createPollTourTargetWhenHandlers(
				tour,
				`[data-tour="${TOUR.artifactReady}"]`
			)
			tour.addStep({
				id: 's11',
				title: text('tour.scrum229.waitArtifactProcessing.title'),
				text: text('tour.scrum229.waitArtifactProcessing.body'),
				attachTo: { element: `[data-tour="${TOUR.filesCard}"]`, on: 'right' },
				canClickTarget: false,
				beforeShowPromise: () =>
					waitForTourTarget(`[data-tour="${TOUR.filesCard}"]`, { timeoutMs: null }),
				when: whenHandlers,
			})
			break
		}
		case 12:
			attachAdvance(
				's12',
				'tour.scrum229.step07.title',
				'tour.scrum229.step07.body',
				`[data-tour="${TOUR.startAnalysis}"]`,
				'bottom'
			)
			break
		case 13: {
			const whenHandlers = createPollModalPrimaryFirstWhenHandlers(
				tour,
				`[data-tour="${TOUR.modalPrimaryFirst}"]`
			)
			tour.addStep({
				id: 's13',
				title: text('tour.scrum229.waitArtifact.title'),
				text: text('tour.scrum229.waitArtifact.body'),
				canClickTarget: true,
				when: whenHandlers,
			})
			break
		}
		case 14:
			attachAdvance(
				's14',
				'tour.scrum229.step08.title',
				'tour.scrum229.step08.body',
				`[data-tour="${TOUR.analysisModal}"]`,
				'right',
				{
					advanceOnClick: false,
					extraHighlights: [`[data-tour="${TOUR.modalPrimaryFirst}"]`],
					beforeShowPromise: () =>
						waitForTourTargets([
							{ selector: `[data-tour="${TOUR.analysisModal}"]` },
							{ selector: `[data-tour="${TOUR.modalPrimaryFirst}"]` },
						]),
				}
			)
			break
		case 15:
			attachAdvance(
				's15',
				'tour.scrum229.step10.title',
				'tour.scrum229.step10.body',
				`[data-tour="${TOUR.analysisModal}"]`,
				'right',
				{
					beforeShowPromise: () =>
						waitForTourTargets([
							{ selector: `[data-tour="${TOUR.analysisModal}"]` },
							{ selector: `[data-tour="${TOUR.modalContextContinue}"]`, requireEnabled: true },
						]),
					advanceOnSelector: `[data-tour="${TOUR.modalContextContinue}"]`,
				}
			)
			break
		case 16:
			attachAdvance(
				's16',
				'tour.scrum229.step12.title',
				'tour.scrum229.step12.body',
				`[data-tour="${TOUR.analysisModal}"]`,
				'right',
				{
					advanceOnSelector: `[data-tour="${TOUR.modalTemplateContinue}"]`,
				}
			)
			break
		case 17:
			attachAdvance(
				's17',
				'tour.scrum229.step14.title',
				'tour.scrum229.step14.body',
				`[data-tour="${TOUR.analysisModal}"]`,
				'left',
				{
					beforeShowPromise: () =>
						waitForTourTargets([
							{ selector: `[data-tour="${TOUR.analysisModal}"]` },
							{ selector: `[data-tour="${TOUR.modalSummaryContinue}"]`, requireEnabled: true },
						]),
					advanceOnSelector: `[data-tour="${TOUR.modalSummaryContinue}"]`,
				}
			)
			break
		case 18:
			attachAdvance(
				's18',
				'tour.scrum229.step15.title',
				'tour.scrum229.step15.body',
				`[data-tour="${TOUR.analysisModal}"]`,
				'right',
				{
					advanceOnSelector: `[data-tour="${TOUR.modalGenerate}"]`,
				}
			)
			break
		case 19: {
			const whenHandlers = createPollTourTargetWhenHandlers(
				tour,
				`[data-tour="${TOUR.analysisStepperNext}"]`,
				{ requireEnabled: true }
			)
			tour.addStep({
				id: 's19',
				title: text('tour.scrum229.analysisGenerating.title'),
				text: text('tour.scrum229.analysisGenerating.body'),
				attachTo: { element: `[data-tour="${TOUR.generatingSummaryContent}"]`, on: 'right' },
				canClickTarget: false,
				beforeShowPromise: () =>
					waitForTourTarget(`[data-tour="${TOUR.generatingSummaryContent}"]`, {
						timeoutMs: null,
					}),
				when: whenHandlers,
			})
			break
		}
		case 20:
			attachAdvance(
				's20',
				'tour.scrum229.step16a.title',
				'tour.scrum229.step16a.body',
				`[data-tour="${TOUR.analysisStepperNext}"]`,
				'top',
				{
					advanceOnSelector: `[data-tour="${TOUR.analysisStepperNext}"]`,
					tourTargetWait: GENERATION_TOUR_WAIT,
				}
			)
			break
		case 21:
			centered('s21', 'tour.scrum229.step16.title', 'tour.scrum229.step16.body', true, {
				beforeShowPromise: () =>
					waitForTourTarget(`[data-tour="${TOUR.summaryRegenerate}"]`, GENERATION_TOUR_WAIT),
			})
			break
		case 22:
			attachAdvance(
				's22',
				'tour.scrum229.step16d.title',
				'tour.scrum229.step16d.body',
				'#section-exceptions',
				'left',
				{
					advanceOnClick: false,
					showNextButton: true,
					extraHighlights: ['#section-contradictions'],
					beforeShowPromise: async () => {
						await waitForElementAndScroll('#section-exceptions', GENERATION_TOUR_WAIT)
						await waitForTourTarget('#section-contradictions', GENERATION_TOUR_WAIT)
					},
				}
			)
			break
		case 23:
			attachAdvance(
				's23',
				'tour.scrum229.step16b.title',
				'tour.scrum229.step16b.body',
				`[data-tour="${TOUR.summaryRegenerate}"]`,
				'left',
				{
					advanceOnClick: false,
					disableTargetClick: true,
					showNextButton: true,
					beforeShowPromise: () =>
						waitForTourTarget(`[data-tour="${TOUR.summaryRegenerate}"]`, GENERATION_TOUR_WAIT),
				}
			)
			break
		case 24:
			attachAdvance(
				's24',
				'tour.scrum229.step16c.title',
				'tour.scrum229.step16c.body',
				`[data-tour="${TOUR.summaryChat}"]`,
				'left',
				{
					advanceOnClick: false,
					disableTargetClick: true,
					showNextButton: true,
					beforeShowPromise: () =>
						waitForTourTarget(`[data-tour="${TOUR.summaryChat}"]`, GENERATION_TOUR_WAIT),
				}
			)
			break
		case 25:
			attachAdvance(
				's25',
				'tour.scrum229.step17.title',
				'tour.scrum229.step17.body',
				`[data-tour="${TOUR.summaryGoToModel}"]`,
				'left',
				{
					tourTargetWait: GENERATION_TOUR_WAIT,
				}
			)
			break
		case 26:
			attachAdvance(
				's26',
				'tour.scrum229.step19.title',
				'tour.scrum229.step19.body',
				'.entry.bpmn-icon-hand-tool[data-action="hand-tool"]',
				'right',
				{
					tourTargetWait: GENERATION_TOUR_WAIT,
				}
			)
			break
		case 27:
			attachAdvance(
				's27',
				'tour.scrum229.step17b.title',
				'tour.scrum229.step17b.body',
				`[data-tour="${TOUR.bpmnCanvas}"]`,
				'left',
				{
					advanceOnClick: false,
					showNextButton: true,
					beforeShowPromise: () =>
						waitForTourTarget(`[data-tour="${TOUR.bpmnCanvas}"]`, GENERATION_TOUR_WAIT),
				}
			)
			break
		case 28:
			attachAdvance(
				's28',
				'tour.scrum229.step19b.title',
				'tour.scrum229.step19b.body',
				'.entry.bpmn-icon-space-tool[data-action="space-tool"]',
				'right',
				{
					tourTargetWait: GENERATION_TOUR_WAIT,
				}
			)
			break
		case 29:
			attachAdvance(
				's29',
				'tour.scrum229.step19c.title',
				'tour.scrum229.step19c.body',
				`[data-tour="${TOUR.bpmnCanvas}"]`,
				'left',
				{
					advanceOnClick: false,
					showNextButton: true,
					beforeShowPromise: () =>
						waitForTourTarget(`[data-tour="${TOUR.bpmnCanvas}"]`, GENERATION_TOUR_WAIT),
				}
			)
			break
		case 30:
			attachAdvance(
				's30',
				'tour.scrum229.step18.title',
				'tour.scrum229.step18.body',
				`[data-tour="${TOUR.bpmnZoomIn}"]`,
				'bottom',
				{
					advanceOnClick: false,
					showNextButton: true,
					beforeShowPromise: () =>
						waitForTourTarget(`[data-tour="${TOUR.bpmnZoomIn}"]`, GENERATION_TOUR_WAIT),
				}
			)
			break
		case 31:
			attachAdvance(
				's31',
				'tour.scrum229.step18b.title',
				'tour.scrum229.step18b.body',
				`[data-tour="${TOUR.bpmnZoomOut}"]`,
				'bottom',
				{
					advanceOnClick: false,
					showNextButton: true,
					beforeShowPromise: () =>
						waitForTourTarget(`[data-tour="${TOUR.bpmnZoomOut}"]`, GENERATION_TOUR_WAIT),
				}
			)
			break
		case 32:
			tour.addStep({
				id: 's32',
				title: text('tour.scrum229.step20.title'),
				text: text('tour.scrum229.step20.body'),
				attachTo: { element: `[data-tour="${TOUR.bpmnExport}"]`, on: 'left' },
				canClickTarget: false,
				beforeShowPromise: () =>
					waitForTourTarget(`[data-tour="${TOUR.bpmnExport}"]`, GENERATION_TOUR_WAIT),
				buttons: [
					{
						text: text('tour.scrum229.buttons.next'),
						classes: 'shepherd-button-primary',
						action() {
							tour.complete()
						},
					},
				],
				when: {
					show: () => {
						document.body.classList.add(TOUR_EXPORT_LOCK_CLASS)
					},
					hide: () => {
						document.body.classList.remove(TOUR_EXPORT_LOCK_CLASS)
					},
				},
			})
			break
		case 33:
			attachAdvance(
				's33',
				'tour.scrum229.step21.title',
				'tour.scrum229.step21.body',
				`[data-tour="${TOUR.modelRegenerate}"]`,
				'left',
				{
					advanceOnClick: false,
					disableTargetClick: true,
					showNextButton: true,
					beforeShowPromise: () =>
						waitForTourTarget(`[data-tour="${TOUR.modelRegenerate}"]`, GENERATION_TOUR_WAIT),
				}
			)
			break
		case 34:
			centered('s34', 'tour.scrum229.step22.title', 'tour.scrum229.step22.body', true, {
				beforeShowPromise: async () => {
					triggerTourConfetti()
				},
			})
			break
	}
}

function createPollTourTargetWhenHandlers(
	tour: ShepherdTour,
	selector: string,
	opts?: { requireEnabled?: boolean }
) {
	const requireEnabled = opts?.requireEnabled ?? false
	let pollingActive = false
	let completed = false
	return {
		show: () => {
			pollingActive = true
			completed = false
			const waitForTarget = () => {
				if (!pollingActive || completed) return
				const el = document.querySelector(selector)
				if (el && isTourTargetVisible(el) && (!requireEnabled || isTourTargetEnabled(el))) {
					completed = true
					pollingActive = false
					if (tour.isActive()) {
						tour.complete()
					}
					return
				}
				requestAnimationFrame(waitForTarget)
			}
			requestAnimationFrame(waitForTarget)
		},
		hide: () => {
			pollingActive = false
		},
	}
}

function createPollModalPrimaryFirstWhenHandlers(tour: ShepherdTour, primarySelector: string) {
	return createPollTourTargetWhenHandlers(tour, primarySelector)
}
